import { useEffect, useMemo } from "react";
import { NavigateFunction, useLocation, useNavigate } from "react-router-dom";
import { BehaviorSubject, distinctUntilChanged, map } from "rxjs";
import { OmitByValue, PickByValue } from "utility-types";

type T = { [key in string]: string | number | boolean | string[] | number[] | Object | null };

const getSearchParams = <T>(p?: URLSearchParams) => {
  const params = p || new URLSearchParams(window.location.search);
  const result: { [key in string]: string } = {} as never;
  for (const [key, value] of params.entries()) {
    // each 'entry' is a [key, value] tupple
    result[key] = value as never;
  }
  return result as T;
};

export class SearchQueryManager<TT extends T> {
  private _search$ = new BehaviorSubject<TT>(this.initValue ?? getSearchParams<TT>());
  public search$ = this._search$.asObservable().pipe(distinctUntilChanged());

  constructor(
    private readonly navigate: NavigateFunction,
    private readonly initValue?: TT, // for testing
  ) {}

  public setSearchQuery = (val: {
    [key in keyof TT]?: string | number | boolean | string[] | number[] | Object | null;
  }) => {
    const search = new URLSearchParams(window.location.search);
    Object.keys(val).map((key) => {
      const value = val[key];
      if (
        value === null ||
        value === undefined ||
        (Array.isArray(value) && (!value.length || value.find((a) => a !== null) === undefined)) ||
        (typeof value === "object" && !Object.keys(value)?.length)
      ) {
        search.delete(key.toString());
      } else if (typeof value === "object" || Array.isArray(value)) {
        search.set(key.toString(), JSON.stringify(value));
      } else if (value !== null && value !== undefined) {
        search.set(key.toString(), value?.toString());
      }
    });
    this.navigate("?" + search.toString(), {
      replace: true,
      relative: "route",
    });
    this._search$.next(getSearchParams(search));
  };
  public updateSearch = (search: string) => this._search$.next(getSearchParams(new URLSearchParams(search)));
  public getString$ = (key: keyof PickByValue<TT, string>, defaultStr?: string) =>
    this.search$.pipe(map((s) => s[key] || defaultStr)).pipe(distinctUntilChanged());
  public getNumber$ = (key: keyof PickByValue<TT, number>, defaultNum?: number) =>
    this.search$.pipe(map((s) => (s[key] ? Number(s[key]) : defaultNum))).pipe(distinctUntilChanged());
  public getBoolean$ = (key: keyof PickByValue<TT, boolean>, defaultBool?: boolean) =>
    this.search$
      .pipe(map((s) => (s[key] ? (s[key] === "true" ? true : false) : defaultBool)))
      .pipe(distinctUntilChanged());
  public getCurrentValue = <TKey extends keyof TT>(key: TKey, defaultVal?: TT[TKey]): string | undefined =>
    (this._search$.value[key] as never) || defaultVal;
  public getCurrentValueObject = <TKey extends keyof TT>(key: TKey, defaultVal?: TT[TKey]) =>
    (this._search$.value
      ? JSON.parse((this._search$.value[key] as never) ?? "null") || defaultVal
      : defaultVal) as TT[TKey];
  public getObject$ = <K extends keyof OmitByValue<TT, string | number | boolean | Date>, X extends TT[K]>(
    key: K,
    defaultObj?: X,
  ) =>
    this.search$.pipe(
      map((s) => (s[key] ? JSON.parse(s[key] as never) : defaultObj) as TT[K]),
      distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
    );
}

export const useQueryManager = <TT extends T>() => {
  const navigate = useNavigate();
  const location = useLocation();
  const bloc = useMemo(() => new SearchQueryManager<TT>(navigate), [navigate]);
  useEffect(() => {
    bloc.updateSearch(location.search);
  }, [location.search]);
  return bloc;
};
